import { isDatabaseInitialized, getItemPriceAsync, getTaxRateAsync, calculateItemPriceAsync } from './database';
import { Decimal } from 'decimal.js';

// Check if IndexedDB is available
export function isSQLiteAvailable(): boolean {
  // Check if window.api exists and database is initialized
  return typeof window !== 'undefined' && window.api !== undefined && isDatabaseInitialized();
}

interface ItemPriceDetails {
  rate: Decimal;
  discount_percent: Decimal;
  discount_amount: Decimal;
  tax_rate_id: number | null;
  tax_amount: Decimal;
  tax_breakdown: Array<{ code: string; rate: Decimal; amount: Decimal }>;
  final_amount: Decimal;
  taxable_amount: Decimal;
  applied_sale_rule_id?: number | null;
  is_tax_inclusive?: boolean;
}

// Get item price details with fallback
export async function getItemPriceDetailsWithFallback(
  itemId: number,
  quantity: number,
  siteId: number | null,
  partyId?: number | null,
  priceListId?: number | null,
  taxRateId?: number | null,
  fallbackFunction?: (itemId: number, quantity: number) => Promise<ItemPriceDetails>
): Promise<ItemPriceDetails> {
  try {
    // Try to get price from IndexedDB with quantity rules
    if (isSQLiteAvailable() && window.api?.calculateItemPrice) {
      const calculatedPrice = await window.api.calculateItemPrice(itemId, quantity, siteId);
      if (calculatedPrice !== null) {
        // Get item details to check if price is tax inclusive
        const { getItemByIdAsync } = await import('./database');
        const item = await getItemByIdAsync(itemId);
        const isTaxInclusive = item?.prices?.is_tax_inclusive ?? false;
        
        console.log(`[integrationUtils] Item ${itemId} tax-inclusive flag:`, isTaxInclusive);
        
        // Get tax rate
        const taxRate = window.api?.getTaxRate ? await window.api.getTaxRate(itemId) : null;
        
        // Build full price details
        const rate = new Decimal(calculatedPrice);
        let taxAmount = new Decimal(0);
        let taxableAmount = rate;
        let finalAmount = rate;
        let taxBreakdown: Array<{ code: string; rate: Decimal; amount: Decimal }> = [];
        
        if (taxRate && taxRate.components) {
          // Calculate tax based on components
          console.log(`[integrationUtils] Calculating tax with rate:`, taxRate);
          
          if (isTaxInclusive) {
            // For tax-inclusive pricing, extract tax from the rate
            const totalTaxRate = taxRate.components.reduce((sum: number, comp: any) => {
              const compRate = typeof comp.rate === 'string' ? parseFloat(comp.rate) : comp.rate;
              return sum + compRate;
            }, 0);
            
            // Calculate tax amount from inclusive price
            taxAmount = rate.mul(totalTaxRate).div(new Decimal(100).plus(totalTaxRate));
            taxableAmount = rate.minus(taxAmount);
            finalAmount = rate; // Final amount stays the same for inclusive pricing
            
            console.log(`[integrationUtils] Tax-inclusive calculation: rate=${rate}, taxRate=${totalTaxRate}%, taxAmount=${taxAmount}, taxableAmount=${taxableAmount}`);
            
            // Break down tax by components
            taxBreakdown = taxRate.components.map((component: any) => {
              const componentRate = typeof component.rate === 'string' ? parseFloat(component.rate) : component.rate;
              const componentAmount = taxAmount.mul(componentRate).div(totalTaxRate);
              console.log(`[integrationUtils] Tax component ${component.code}: ${componentRate}% = ${componentAmount}`);
              return {
                code: component.code,
                rate: new Decimal(componentRate),
                amount: componentAmount
              };
            });
          } else {
            // For tax-exclusive pricing, add tax on top
            taxBreakdown = taxRate.components.map((component: any) => {
              const componentRate = typeof component.rate === 'string' ? parseFloat(component.rate) : component.rate;
              const componentAmount = rate.mul(componentRate).div(100);
              taxAmount = taxAmount.plus(componentAmount);
              console.log(`[integrationUtils] Tax component ${component.code}: ${componentRate}% of ${rate} = ${componentAmount}`);
              return {
                code: component.code,
                rate: new Decimal(componentRate),
                amount: componentAmount
              };
            });
            finalAmount = rate.plus(taxAmount);
            console.log(`[integrationUtils] Tax-exclusive calculation: Total tax amount: ${taxAmount}`);
          }
        }
        
        return {
          rate,
          discount_percent: new Decimal(0),
          discount_amount: new Decimal(0),
          tax_rate_id: taxRate?.id || taxRateId || null,
          tax_amount: taxAmount,
          tax_breakdown: taxBreakdown,
          final_amount: finalAmount,
          taxable_amount: taxableAmount,
          applied_sale_rule_id: null,
          is_tax_inclusive: isTaxInclusive
        };
      }
    }
    
    // If IndexedDB failed or returned null, try fallback function
    if (fallbackFunction) {
      return await fallbackFunction(itemId, quantity);
    }
    
    // Default empty response
    return {
      rate: new Decimal(0),
      discount_percent: new Decimal(0),
      discount_amount: new Decimal(0),
      tax_rate_id: taxRateId || null,
      tax_amount: new Decimal(0),
      tax_breakdown: [],
      final_amount: new Decimal(0),
      taxable_amount: new Decimal(0),
      applied_sale_rule_id: null,
      is_tax_inclusive: false
    };
  } catch (error) {
    console.error('[integrationUtils] Error getting item price:', error);
    
    // Try fallback function on error
    if (fallbackFunction) {
      try {
        return await fallbackFunction(itemId, quantity);
      } catch (fallbackError) {
        console.error('[integrationUtils] Fallback function also failed:', fallbackError);
      }
    }
    
    // Return safe defaults
    return {
      rate: new Decimal(0),
      discount_percent: new Decimal(0),
      discount_amount: new Decimal(0),
      tax_rate_id: taxRateId || null,
      tax_amount: new Decimal(0),
      tax_breakdown: [],
      final_amount: new Decimal(0),
      taxable_amount: new Decimal(0),
      applied_sale_rule_id: null,
      is_tax_inclusive: false
    };
  }
}